import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadSelector } from './DownloadSelector';
import { ProductRow } from './ProductRow';

import { SearchProductsForm } from '@/components/SearchProductsForm';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
} from '@/components/ui/Table';
import { withSearchProductsFilters } from '@/hocs/withSearchProductsFilter';
import { useGetProducts } from '@/hooks/useGetProducts';
import { capitalizeWords } from '@/lib/utils';

export const ProductsTable = withSearchProductsFilters(
  ({ searchFilters, handleSearchFilters }) => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'pages.SeoContent.components.ProductsTable',
    });
    const endTableRef = useRef<HTMLDivElement>(null);

    const { products, isFetchingNextPage, hasNextPage, fetchNextPage } =
      useGetProducts(searchFilters);

    const toneOfVoiceHeaders: string[] =
      products
        .find((p) => p.visionOutput?.product_attributes?.voice_tone)
        ?.visionOutput?.product_attributes?.voice_tone?.map(
          (item: any) => item?.property,
        )
        ?.filter(Boolean) ?? [];

    useEffect(() => {
      if (!endTableRef.current) {
        return;
      }

      const observer = new IntersectionObserver(
        (entries, obs) => {
          const { isIntersecting } = entries[0];

          if (!hasNextPage) {
            obs.disconnect();
            return;
          }

          if (isIntersecting && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          rootMargin: '30px',
        },
      );

      observer.observe(endTableRef.current);

      return () => {
        observer.disconnect();
      };
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    return (
      <div className="space-y-4">
        <DownloadSelector />

        <SearchProductsForm
          searchFilters={searchFilters}
          onSearchFilters={handleSearchFilters}
        />

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('id')}</TableHead>
                <TableHead>{t('name')}</TableHead>
                <TableHead>{t('sku')}</TableHead>
                <TableHead>{t('image')}</TableHead>
                <TableHead>{t('description')}</TableHead>
                <TableHead>{t('seoDescription')}</TableHead>
                <TableHead>{t('tags')}</TableHead>

                {toneOfVoiceHeaders.length > 0 &&
                  toneOfVoiceHeaders.map((h) => (
                    <TableHead key={h}>
                      {t('toneOfVoice')}{' '}
                      {capitalizeWords(h?.replaceAll('_', ' '))}
                    </TableHead>
                  ))}

                {Array.from({ length: 10 }).map((_, index) => (
                  <TableHead key={index}>
                    {t('altDescriptionImage')} {index + 1}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {products.map((product) => {
                if (!product.formattedSeo) {
                  return null;
                }

                const toneOfVoiceNames: string[] =
                  product?.visionOutput?.product_attributes?.voice_tone
                    ?.map((item: any) => item?.name)
                    ?.filter(Boolean) ?? [];

                return (
                  <ProductRow
                    key={product.id}
                    internalId={product.id}
                    productId={product.productId}
                    sku={product.sku}
                    formattedSeo={product.formattedSeo}
                    toneOfVoiceHeaders={toneOfVoiceHeaders}
                    toneOfVoiceNames={toneOfVoiceNames}
                  />
                );
              })}
            </TableBody>

            <TableCaption ref={endTableRef}>
              {isFetchingNextPage && (
                <Loader2 className="mx-auto my-4 animate-spin" />
              )}
            </TableCaption>
          </Table>
        </div>
      </div>
    );
  },
);
